import { LOWERCASED_UUID_PATTERN } from "./deployment-config.js";
import { blobBucketPhysicalSchema } from "./blob-bindings.js";
import { RESOURCE_SLUG_PATTERN } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/platform-buckets.ts
/**
* Per-org platform buckets: the CP-to-DP desired-state member, the DP file and the lookup
* (2026-10-13-blob-buckets, stage 2a contracts, rulings R-027, R-028, R-030).
*
* Each org on a data plane gets two platform buckets, `platform` and `audit`
* (`PLATFORM_BUCKET_KINDS` of `@zion/sdk-contracts`). Only platform accounts reach them, never an
* app account (R-029). The chain:
*
* 1. release-engine (WS-27) allocates the two buckets of each resident org of a data plane in
*    `PLATFORM_BUCKET_DP_IDS`. It writes {@link PlatformBuckets}, keyed by org slug, into
*    `DesiredStateEnvelope.platformBuckets`, `ready` rows only (R-028 item 3). It writes `{}` for a
*    data plane that holds platform bucket rows and no ready org, and leaves the member out on its
*    own fault or for a data plane with no row (R-040 item 2, R-041 item 2).
* 2. The operator program (WS-12) keeps its last ConfigMap when the member is absent, and deletes
*    the ConfigMap when the member is `{}` (R-040 item 1). For a member with orgs, it maps each slug
*    to its DP-local org id, and renders
*    {@link PlatformBucketsFile} into the ConfigMap {@link PLATFORM_BUCKETS_CONFIGMAP} under the key
*    {@link PLATFORM_BUCKETS_CONFIGMAP_KEY} (R-030 item 1). It stamps the blob-proxy annotation
*    {@link BLOB_PROXY_BUCKET_ANNOTATION} on each attempt (R-030 item 3).
* 3. core-api (WS-11, mounted by WS-29) and the audit archiver and reader (S2b) read the file at
*    {@link PLATFORM_BUCKETS_MOUNT_PATH}, named by {@link PLATFORM_BUCKETS_FILE_ENV}, and look up
*    an org with {@link resolvePlatformBucket}. No file, or no row for the org, keeps the legacy path
*    (R-030 item 5).
*
* Two id spaces: the member keys an org by slug, the cross-plane key (R-027 item 3). The file keys
* it by the DP-local org id (`auth.organizations.id`), the id a DP service holds at each write.
*
* The shared fixtures `cloud/platform-sdk/fixtures/platform-buckets/` hold valid and invalid files
* and the expected lookups; the TypeScript test and the Rust readers of S2b read them.
*
* Pure: no clock, no filesystem, no network.
*/
/**
* The most orgs one {@link PlatformBuckets} member or one {@link PlatformBucketsFile} holds:
* 10,000. A refusal backstop on a CP-written map and an operator-written file.
*
* The live bound is lower. The file goes into one ConfigMap, and the operator program holds the
* ConfigMap as it was when a render is over its size limit (WS-12). One org row of the longest
* `gcs` shape, both buckets set, is 445 bytes of compact JSON with its comma, so a file of
* 1,000,000 bytes holds 2,247 such rows. One row of the longest `s3-compatible` shape is 1,033
* bytes, so such a file holds 968 rows.
*/
const PLATFORM_BUCKETS_MAX_ORGS = 1e4;
/** The name of the ConfigMap the operator program writes in the data plane namespace (R-030 item 1). */
const PLATFORM_BUCKETS_CONFIGMAP = "zion-platform-buckets";
/**
* The data key of {@link PLATFORM_BUCKETS_CONFIGMAP} that holds the {@link PlatformBucketsFile}, and
* the file name the chart mounts (R-030 item 1). The basename of {@link PLATFORM_BUCKETS_MOUNT_PATH}.
*/
const PLATFORM_BUCKETS_CONFIGMAP_KEY = "platform-buckets.json";
/** The env var that names the platform buckets file inside a platform pod (R-030 item 1). */
const PLATFORM_BUCKETS_FILE_ENV = "ZION_PLATFORM_BUCKETS_FILE";
/**
* The path the chart mounts the platform buckets file at, read-only, with `optional: true`
* (WS-29). It sits under `/etc/zion`, out of the signer sidecar's read-only `/var/run/zion` mount,
* as `BLOB_BINDINGS_MOUNT_PATH` does (R-008).
*/
const PLATFORM_BUCKETS_MOUNT_PATH = "/etc/zion/platform-buckets/platform-buckets.json";
/**
* The pod annotation that names the bucket of one blob-proxy attempt (R-030 item 3). The operator
* program stamps the org `platform` bucket when the file holds the org, and the legacy bucket
* otherwise (WS-12). The blob proxy reads the bucket from it, and uses `BLOB_PROXY_BUCKET` for an
* attempt with no annotation (WS-13).
*/
const BLOB_PROXY_BUCKET_ANNOTATION = "zion.witness.ai/blob-bucket";
/** The one version of {@link PlatformBucketsFile}. A reader refuses every other value. */
const PLATFORM_BUCKETS_FILE_VERSION = 1;
/**
* The age, in days, of the two lifecycle delete rules that release-engine puts on each org
* `platform` bucket: one on prefix `workruns/` and one on prefix `mail/` (R-042). They are
* backstops for objects that a consumer stranded, as `artifactStorage.backstopDays` is for the
* legacy work artifact bucket. The operator program refuses to boot unless
* `WORK_RUN_RETENTION_SECONDS` is below `(PLATFORM_BUCKET_BACKSTOP_DAYS - 1) * 86400`.
*/
const PLATFORM_BUCKET_BACKSTOP_DAYS = 30;
/** An org slug: 2 to 63 characters in `RESOURCE_SLUG_PATTERN`. The cap runs before the pattern. */
const orgSlugSchema = z.string().min(2).max(63).regex(RESOURCE_SLUG_PATTERN, "an org slug of lowercase letters, numbers and hyphens");
/** The two buckets of one org: `platform`, and `audit` when its row is ready. */
const pairShape = {
	platform: blobBucketPhysicalSchema,
	audit: blobBucketPhysicalSchema.optional()
};
/** The physical bucket names of `pairs`, in order: `platform`, then `audit` when present, per pair. */
function bucketNames(pairs) {
	return pairs.flatMap((pair) => pair.audit === void 0 ? [pair.platform.bucket] : [pair.platform.bucket, pair.audit.bucket]);
}
/** `true` when no physical bucket name of `pairs` appears twice. */
function bucketsDistinct(pairs) {
	const names = bucketNames(pairs);
	return new Set(names).size === names.length;
}
const BUCKETS_DISTINCT_MESSAGE = "every physical bucket appears once: no two orgs and no two kinds share a bucket";
/**
* The member `DesiredStateEnvelope.platformBuckets` (R-028 item 3): org slug to its
* {@link PlatformBucketPair}. Strict at every depth.
*
* Valid input:
* - 0 to {@link PLATFORM_BUCKETS_MAX_ORGS} keys. `{}`, the map with no key, is the empty member
*   (R-041 item 2).
* - each key is an org slug: 2 to 63 characters in `RESOURCE_SLUG_PATTERN`.
* - each value is `{ platform, audit? }` of `blobBucketPhysicalSchema`. An unknown key fails.
* - every physical bucket name appears once in the whole member: the `platform` and the `audit`
*   bucket of one org differ, and no two orgs share a bucket (defaults row F3). A repeat fails at
*   the root with the message of the rule.
*
* Absent and empty are two different messages (R-040 items 1 and 2, R-041 item 2):
*
* | member  | meaning                                                         | operator program (WS-12) |
* |---------|-----------------------------------------------------------------|--------------------------|
* | absent  | no news: the CP sent no routing this cycle, through its own fault or for a data plane with no platform bucket row | keeps its last ConfigMap and its holder |
* | `{}`    | the CP holds platform bucket rows for this data plane, and no org has a ready `platform` row | deletes the ConfigMap and clears its holder |
* | 1 or more keys | the ready platform buckets of those orgs                | renders the file of every org whose slug names a DP-local org |
*
* The producer rules (WS-27). The producer must:
* 1. put an org in the member only when its `platform` row is `ready`, and add `audit` only when
*    its `audit` row is `ready`. An org with a ready `audit` row and no ready `platform` row is
*    absent;
* 2. sort the keys by slug. The member is desired-state etag input. This schema does not check the
*    order, as `appManifestsByVersion` does not;
* 3. leave out an org whose slug fails the slug grammar, and log it. One bad key fails the whole
*    member. An invalid member is no routing either: the operator program keeps its last ConfigMap
*    and its holder, as for an absent member (R-040 item 1);
* 4. emit the member for each data plane that holds any `release.platform_buckets` row, with `{}`
*    when no org is ready, also for a data plane outside `PLATFORM_BUCKET_DP_IDS` (R-041 item 4).
*    It leaves the member out only on its own fault (`GCP_PROJECT` unset, a member that this
*    schema refuses) or for a data plane with no row (R-040 item 2). An absent member never
*    deletes stored routing.
*
* No `.transform()`: the output equals the input.
*/
const platformBucketsSchema = z.record(orgSlugSchema, z.object(pairShape).strict()).refine((orgs) => Object.keys(orgs).length <= PLATFORM_BUCKETS_MAX_ORGS, { message: `at most ${PLATFORM_BUCKETS_MAX_ORGS} orgs` }).refine((orgs) => bucketsDistinct(Object.values(orgs)), {
	message: BUCKETS_DISTINCT_MESSAGE,
	when: (payload) => payload.issues.length === 0
});
/**
* The file the operator program writes into {@link PLATFORM_BUCKETS_CONFIGMAP} (R-030 item 1).
* `version` is {@link PLATFORM_BUCKETS_FILE_VERSION}; `orgs` lists one row per org. Strict at every
* depth: a reader refuses an unknown key, any other version and a malformed row, whole. It never
* repairs a file.
*
* Valid input:
* - `orgs` holds 0 to {@link PLATFORM_BUCKETS_MAX_ORGS} rows. An empty list is valid: the data
*   plane holds no DP-local row for any org of the member.
* - each row is `{ slug, dpOrgId, platform, audit? }`. `slug` is an org slug (2 to 63 characters in
*   `RESOURCE_SLUG_PATTERN`); `dpOrgId` is a lower-case UUID.
* - the rows are sorted by `slug`, strictly ascending in code-unit order, so no slug appears twice.
*   A row out of order fails at `orgs/<index>/slug` of that row.
* - no `dpOrgId` appears twice. A repeat fails at `orgs/<index>/dpOrgId` of the repeat.
* - every physical bucket name appears once in the file, as in {@link platformBucketsSchema}.
*
* The producer rule (WS-12): the operator program writes one row per org of the member whose slug
* names a DP-local org, and leaves out an org with no DP-local row (R-030 item 1).
*/
const platformBucketsFileSchema = z.object({
	version: z.literal(1),
	orgs: z.array(z.object({
		slug: orgSlugSchema,
		dpOrgId: z.string().regex(LOWERCASED_UUID_PATTERN, "a lower-case UUID org id"),
		...pairShape
	}).strict()).max(PLATFORM_BUCKETS_MAX_ORGS).superRefine((orgs, ctx) => {
		const ids = /* @__PURE__ */ new Set();
		orgs.forEach((org, index) => {
			const previous = index === 0 ? void 0 : orgs[index - 1];
			if (previous !== void 0 && !(previous.slug < org.slug)) ctx.addIssue({
				code: "custom",
				path: [index, "slug"],
				message: "orgs must be sorted by slug, each slug once"
			});
			if (ids.has(org.dpOrgId)) ctx.addIssue({
				code: "custom",
				path: [index, "dpOrgId"],
				message: "a dpOrgId appears twice"
			});
			ids.add(org.dpOrgId);
		});
	}).refine((orgs) => bucketsDistinct(orgs), {
		message: BUCKETS_DISTINCT_MESSAGE,
		when: (payload) => payload.issues.length === 0
	})
}).strict();
/**
* The bucket of kind `kind` of the org `dpOrgId` in `file`, or `undefined`.
*
* Callers: core-api (WS-11) at each mail attachment write, read and delete; the audit archiver and
* reader (S2b) through their Rust port of the same rule.
*
* 1. The match on `dpOrgId` ignores case: the lookup lower-cases `dpOrgId`, and every `dpOrgId` of
*    a valid file is lower case.
* 2. No row of that org: `undefined`. The caller then keeps its legacy path (R-030 item 5).
* 3. `kind` is `platform`: the row's `platform` bucket.
* 4. `kind` is `audit`: the row's `audit` bucket, or `undefined` when the row has none.
*
* `file` must be the output of {@link platformBucketsFileSchema}. Pure; never throws.
*/
function resolvePlatformBucket(file, dpOrgId, kind) {
	const wanted = dpOrgId.toLowerCase();
	const org = file.orgs.find((row) => row.dpOrgId === wanted);
	if (org === void 0) return void 0;
	return kind === "audit" ? org.audit : org.platform;
}
//#endregion
export { BLOB_PROXY_BUCKET_ANNOTATION, PLATFORM_BUCKETS_CONFIGMAP, PLATFORM_BUCKETS_CONFIGMAP_KEY, PLATFORM_BUCKETS_FILE_ENV, PLATFORM_BUCKETS_FILE_VERSION, PLATFORM_BUCKETS_MAX_ORGS, PLATFORM_BUCKETS_MOUNT_PATH, PLATFORM_BUCKET_BACKSTOP_DAYS, platformBucketsFileSchema, platformBucketsSchema, resolvePlatformBucket };

//# sourceMappingURL=platform-buckets.js.map